import { Directive, inject, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import {
  InstanceManagerService,
  PlaybackService,
  SessionsManagerService,
  TimelineService,
} from '@splixer/player';
import { PSNotification, PSNotificationTypes } from '@hidat/huijs-player';
import { AnyHash } from '@hidat/huijs-common';
import { V2BasePageController } from './base-page.controller';
import { HngMessageNotifierService } from '@hidat/ng-common';

/**
 * Base page for a player app's home/landing page
 */
@Directive()
export abstract class Pv2StationHomePageController
  extends V2BasePageController
  implements OnInit
{
  protected psNotifier$: Subscription;
  protected notify = inject(HngMessageNotifierService);
  protected stationClasses: string[] = [];
  protected timeline = inject(TimelineService);
  protected session = inject(SessionsManagerService);
  protected instance = inject(InstanceManagerService);
  protected playbackService = inject(PlaybackService);
  protected router = inject(Router);

  protected showPlaybackModal = signal(false);
  protected showLoadingModel = signal(false);
  protected showHeadlines = signal(false);
  protected mixStarted = signal(false);
  protected showNowPlaying = signal(false);
  protected npMessage = signal<string | undefined>(undefined);
  protected knownInstance = signal(false);
  protected pageKlasses: AnyHash = {
    'known-instance': false,
    'unknown-instance': true,
    'content-started': false,
  };

  constructor() {
    super();
    this.psNotifier$ = this.playbackService.notifier.subscribe((notification) =>
      this.handleNotification(notification),
    );
  }

  ngOnInit() {
    if (this.station.stationCode) {
      this.setPageTitle('Home', true);
      this.stationClasses.push(this.station.stationCode.toLowerCase());
      this.stationLoaded();
      this.knownInstance.set(this.instance.loaded());
      this.pageKlasses['known-instance'] = this.knownInstance;
      this.pageKlasses['unknown-instance'] = !this.knownInstance;
    } else {
      this.npMessage.set('No Station Content Loaded!');
    }
  }

  /**
   * Handle Playback Error
   * Notifies the user of playback errors.
   * Primary purpose is to give a 'Press Play' modal when browser stops us.
   * @param notification
   */
  protected handleNotification(notification: PSNotification): void {
    console.log(notification.message);
    switch (notification.type) {
      case PSNotificationTypes.MIX_LOADING:
        this.npMessage.set('Loading Mix...');
        this.showLoadingModel.set(true);
        this.pageKlasses['content-started'] = true;
        // At this point, we have to have an instance
        this.knownInstance.set(true);
        this.pageKlasses['known-instance'] = true;
        this.pageKlasses['unknown-instance'] = false;
        break;
      case PSNotificationTypes.MIX_LOADED:
        this.showLoadingModel.set(false);
        this.npMessage.set('Mix Loaded');
        break;
      case PSNotificationTypes.PLAYBACK_ERROR:
        this.displayPlayModal();
        break;
      case PSNotificationTypes.OUT_OF_CONTENT:
        this.timeline.forceNowPlayingToPast();
        this.npMessage.set('Please Choose Something to Listen To');
        this.showNowPlaying.set(false);
        this.mixStarted.set(false);
        break;
      case PSNotificationTypes.ALMOST_OUT_OF_CONTENT:
        //this.npMessage.set("No more content available.");
        //this.notify.warn("No more content available.");
        break;
      case PSNotificationTypes.OUT_OF_SKIPS:
        this.notify.error(
          "We're sorry, but you have run out of skips for this hour.",
          {},
          'Audio Player',
        );
        break;
      case PSNotificationTypes.LOAD_ERROR:
      case PSNotificationTypes.ERROR:
        this.npMessage.set(notification.message);
        this.showNowPlaying.set(false);
        this.mixStarted.set(false);
        this.notify.error(notification.message, {}, 'Audio Player');
        break;
      case PSNotificationTypes.INFO:
        this.notify.info(notification.message, {}, 'Audio Player');
        break;
      case PSNotificationTypes.FIRST_MIX_STARTED:
        this.showLoadingModel.set(false);
        this.pageKlasses['content-started'] = true;
        this.showNowPlaying.set(true);
        this.mixStarted.set(true);
        break;
      case PSNotificationTypes.MIX_STARTED:
        this.onMixStarted();
        break;
    }
  }

  protected abstract onMixStarted(): void;

  protected displayPlayModal() {
    this.showPlaybackModal.set(true);
  }

  public modalPlay = (event?: Event) => {
    this.playbackService.togglePlayback('modal_play');
    this.showPlaybackModal.set(false);
    if (event) {
      event.preventDefault();
    }
  };

  protected abstract stationLoaded(): void;
}
